import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthUser extends User {
  role?: 'patient' | 'agency_admin' | 'super_admin' | 'supervisor';
  agency_id?: string;
  agency?: {
    id: string;
    name: string;
    code: string;
  };
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    if (!session?.user) return;

    try {
      const [profileResult, rolesResult] = await Promise.all([
        supabase
          .from("profiles")
          .select(`
            *,
            agencies(id, name, code)
          `)
          .eq("user_id", session.user.id)
          .single(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id),
      ]);

      const { data: profile } = profileResult;
      const roles = rolesResult.data?.map((r) => r.role) ?? [];

      if (profile) {
        // Determine effective role from user_roles (source of truth)
        // Priority: super_admin > agency_admin > supervisor > patient
        const effectiveRole: AuthUser["role"] | undefined = roles.includes("super_admin")
          ? "super_admin"
          : roles.includes("agency_admin")
            ? "agency_admin"
            : roles.includes("supervisor")
              ? "supervisor"
              : roles.length > 0 ? "patient" : undefined;

        // Get patient data only for patients
        let patientData = null;
        if (effectiveRole === "patient") {
          const { data: patient } = await supabase
            .from("patients")
            .select("id, first_name, last_name")
            .eq("user_id", session.user.id)
            .single();
          patientData = patient;
        }

        setUser({
          ...session.user,
          role: effectiveRole,
          agency_id: profile.agency_id,
          agency: profile.agencies,
          patient: patientData,
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      // Keep the basic user that was already set
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        if (session?.user) {
          // Set user immediately from session
          setUser({
            ...session.user,
            role: undefined,
            agency_id: undefined,
            agency: undefined,
            patient: null
          });
          
          // Then try to load profile data in background
          setTimeout(() => {
            refreshProfile();
          }, 100);
        } else {
          setUser(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      
      if (session?.user) {
        // Set user immediately from session
        setUser({
          ...session.user,
          role: undefined,
          agency_id: undefined,
          agency: undefined,
          patient: null
        });
        
        // Then try to load profile data in background
        setTimeout(() => {
          refreshProfile();
        }, 100);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Try global sign out first; if that fails (e.g. session_not_found), ensure we still clear locally.
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.warn('Global sign out failed, clearing local session:', error.message);
      await supabase.auth.signOut({ scope: 'local' });
    }

    // Ensure local state is cleared immediately
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};